// The window of time the map is narrowed to, and the reading of dates behind it.
//
// Dates reach us as YYYY-MM-DD strings in one dataset column, and stay strings
// throughout: that format sorts chronologically as text, so comparing two dates
// needs no arithmetic, no clock and no time zone. It is also what the querystring
// carries and what a native date field speaks.
//
// Whether the range touches a point at all is its layer's declaration, not the
// point's: an asset layer such as critical infrastructure does not vanish
// because a reader narrowed to last month.

// The dataset's date column. Named here rather than configured, the way the
// lat/long columns are constants on the backend today. All of them want
// reconciling in one go when the real dataset lands.
export const DATE_PROPERTY = 'date';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A date cell as YYYY-MM-DD, or null if it is not a date.
 *
 * Read as UTC rather than local time, so which day an incident falls on is the
 * same for a reader in Auckland and one in Los Angeles.
 */
export const toDate = (value) => {
    const match = ISO_DATE.exec(typeof value === 'string' ? value.trim() : '');

    if (!match) {
        return null;
    }

    const [, year, month, day] = match.map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));

    // Date.UTC rolls 2026-02-31 forward into March. A day that does not exist is
    // a broken cell, not a date a month away from where it was written.
    if (parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
        return null;
    }

    return parsed.toISOString().slice(0, 10);
};

// The layers that declared themselves dated — the only ones the range applies
// to.
const datedMatchValues = (config) =>
    new Set(
        config.layers
            .filter((layer) => layer.isDated)
            .map((layer) => layer.matchValue)
    );

// A feature's date, or null when the range has no business filtering it: a point
// in an undated layer, or one whose date cell cannot be read.
const featureDate = (feature, dated, column) => {
    if (!dated.has(feature.properties?.[column])) {
        return null;
    }

    return toDate(feature.properties?.[DATE_PROPERTY]);
};

/**
 * Every date the data has an event on, each once, oldest first.
 *
 * Taken from every configured point rather than the visible ones, the way the
 * extent is: a control built on these must not change under a reader who
 * switches a layer off.
 */
export const selectEventDates = (points, config) => {
    const dated = datedMatchValues(config);
    const dates = (points?.features || [])
        .map((feature) => featureDate(feature, dated, config.column))
        .filter((date) => date !== null);

    return [...new Set(dates)].sort();
};

/**
 * The full extent of the loaded data, which is what the control spans.
 *
 * Null when nothing carries a readable date: with no extent there is no range
 * worth offering.
 */
export const selectDateExtent = (points, config) => {
    const dates = selectEventDates(points, config);

    if (!dates.length) {
        return null;
    }

    return { min: dates[0], max: dates[dates.length - 1] };
};

/**
 * The points inside the selected window, both ends included.
 *
 * Filtered in the browser because the upstream API offers equality and
 * inequality and no range operators — forced rather than chosen, and fine at
 * this volume.
 */
export const selectPointsInDateRange = (points, config, range) => {
    if (!points) {
        return null;
    }

    if (!range) {
        return points;
    }

    const dated = datedMatchValues(config);

    return {
        type: 'FeatureCollection',
        features: points.features.filter((feature) => {
            const date = featureDate(feature, dated, config.column);

            return date === null || (date >= range.from && date <= range.to);
        }),
    };
};
